// Angular + External
import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";

import {
  SystemMessagePromiseService,
  SystemMessageType,
  ActiveUserProfilePromiseService,
  UserPromiseLookupService,
  SystemConfigPromiseService,
  SysFeatures,
  NotificationService,
  BFXNotificationEventType,
  BFXNotificationType,
  INotificationEvent,
} from "shared/cubiq-services";
import { UserAuthPromiseService } from "../../../shared/cubiq-services/auth/impl/UserAuthPromiseService";
import { BfxAuthService } from "./bfx-auth.service";
import { BroadcastMessEventImpl, SystemMessage } from "shared/cubiq-model-multi-asset";
import { BfxDialogData } from "../../bfx-dialogs/models/bfx-dialog-data.model";
import { BfxDialogCommand } from "../../bfx-dialogs/models/bfx-dialog-command.enum";
import { TranslateService } from "@ngx-translate/core";
import { LoggerService } from "@bfx/logger";
import { GoldenLayoutService } from "../../golden-layout/golden-layout-service";
const logger = LoggerService.for(__dirname);

const DEFAULT_RESTART_TIME: number = 10;
const MINIMAL_RESTART_TIME: number = 10;

@Injectable()
export class BfxSystemMessageService {
  private systemMessages$: Observable<SystemMessage>;
  private restartDialogSubject = new Subject<BfxDialogData>();
  public restart$: Observable<BfxDialogData> = this.restartDialogSubject.asObservable();
  private restartDialogTitle: string;

  private restartTimeout: number = DEFAULT_RESTART_TIME;
  private restartActive: boolean = false;

  constructor(
    private _bfxAuthService: BfxAuthService,
    private _systemMessageService: SystemMessagePromiseService,
    private activeUserService: ActiveUserProfilePromiseService,
    private translationService: TranslateService,
    protected _userAuthService: UserAuthPromiseService,
    protected goldenLayoutService: GoldenLayoutService,
    protected systemConfigService: SystemConfigPromiseService,
    private userService: UserPromiseLookupService,
    private notificationService: NotificationService,
  ) {
    this.restartDialogTitle = this.translationService.instant(
      "system_message.dialog.title.generic",
    );

    this.activeUserService.getActiveUserData().then((activeUser) => {
      if (activeUser.isTrader()) {
        this.restartDialogTitle = this.translationService.instant(
          "system_message.dialog.title.trader",
        );
      }
      if (activeUser.isSales()) {
        this.restartDialogTitle = this.translationService.instant(
          "system_message.dialog.title.sales",
        );
      }
    });

    this.systemConfigService
      .getSystemConfig(SysFeatures.maxProfileChangeTimeout)
      .then((value: string) => {
        if (value != null) {
          try {
            this.restartTimeout = Math.max(MINIMAL_RESTART_TIME, parseInt(value, 10));
          } catch (e) {
            logger.out.warn(
              this.constructor.name +
                ": Could not read value of property " +
                SysFeatures.maxProfileChangeTimeout +
                ": " +
                value,
            );
          }
        }
      });

    this.systemMessages$ = this._systemMessageService.getSystemMessages();

    this.systemMessages$.subscribe(
      (systemMessage?: SystemMessage) => {
        if (SystemMessageType[systemMessage.messageType] == SystemMessageType.SESSION_PURGE) {
          this._bfxAuthService.forceLogout();
        } else if (
          SystemMessageType[systemMessage.messageType] == SystemMessageType.USER_PROFILE_CHANGE &&
          this.restartActive === false
        ) {
          this.restartActive = true;
          logger.out.info(
            this.constructor.name + ": Received user profile change event. Triggerring restart ...",
          );

          // Make sure we have the interface loaded before showing the restart dialog
          this.goldenLayoutService.isReady().then(() => {
            this.restartDialogSubject.next(
              new BfxDialogData(BfxDialogCommand.DIALOG_RESTART_CONFIRM, null, null, {
                title: this.restartDialogTitle,
                restartTimeout: this.restartTimeout,
                messages: [this.translationService.instant("system_message.dialog.message")],
                submessage: this.translationService.instant("system_message.dialog.submessage"),
                callback: () => {
                  this._bfxAuthService.restartLogout();
                },
              }),
            );
          });
        } else if (
          SystemMessageType[systemMessage.messageType] == SystemMessageType.BROADCAST_MESSAGE
        ) {
          try {
            if (systemMessage.message instanceof BroadcastMessEventImpl) {
              let broadcastMessage: BroadcastMessEventImpl =
                systemMessage.message as BroadcastMessEventImpl;
              if (broadcastMessage.message != null && broadcastMessage.message.length > 0) {
                logger.out.info(`${this.constructor.name}: Received broadcast message.`);
                let eventData: INotificationEvent =
                  this.notificationService.simpleMessageToNotification(
                    broadcastMessage.message,
                    BFXNotificationType.MESSAGE,
                  );
                this.notificationService.fireNotificationEvent(
                  eventData,
                  BFXNotificationEventType.MESSAGE,
                );
              } else {
                logger.out.warn(
                  `${this.constructor.name}: Not showing broadcast message because of missing message payload.`,
                );
              }
            }
          } catch (e) {
            logger.out.error(
              `${this.constructor.name}: Failed to convert broadcast message into notification ${e}`,
            );
          }
        }
      },
      (error?) => {
        logger.out.warn(`${this.constructor.name} subject error %O`, error);
      },
    );
  }
}
